import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { uuidv7 } from 'uuidv7';
import { z } from 'zod';
import { useCloneBudgetApi } from '../api/use-clone-budget';
// Budget Clone Form Schema
export const budgetCloneFormSchema = z.object({
    name: z.string().min(1, 'Budget name is required'),
    startOver: z.boolean().optional(),
});
// Budget Clone Form Hook
export const useBudgetCloneForm = ({ budgets, budget, onSuccess }) => {
    // mutations
    const { mutate: cloneBudget, isPending: isClonePending } = useCloneBudgetApi();
    // enhance schema with name uniqueness validation and trimming
    const schema = budgetCloneFormSchema.refine((data) => {
        const trimmedName = data.name?.trim() || '';
        const isNameTaken = budgets.some((b) => b.name.trim().toLowerCase() === trimmedName.toLowerCase() && b.id !== budget.id);
        return !isNameTaken;
    }, {
        message: 'A budget with this name already exists',
        path: ['name'],
    });
    const defaultValues = useMemo(() => toFormData(budget), [budget]);
    const form = useForm({
        resolver: zodResolver(schema),
        defaultValues,
    });
    useEffect(() => {
        form.reset(defaultValues);
    }, [budget, form, defaultValues]);
    /**
     * Handles the form submission event, building the API payload and
     * invoking the clone mutation.
     *
     * @param values - Sanitized budget clone form values.
     */
    const submitForm = useCallback((values) => {
        const payload = toRequestData(values, budget);
        cloneBudget(payload, {
            onSuccess: (data) => {
                onSuccess?.(data);
            },
            onError: (error) => {
                console.error('Error cloning budget:', error);
            },
        });
    }, [budget, cloneBudget, onSuccess]);
    return {
        form,
        onSubmit: form.handleSubmit(submitForm),
        isSubmitting: isClonePending,
    };
};
/**
 * Compute the initial values for the BudgetCloneForm.
 *
 * Generates default values with a cloned name and default settings.
 *
 * @param budget - The existing Budget to clone
 * @returns A fully populated BudgetCloneFormRequest
 */
function toFormData(budget) {
    return {
        name: `${budget.name} Clone`,
        startOver: false,
    };
}
/**
 * Build the BudgetRequestClone payload from form values and context.
 *
 * Merges form values with required fields to create a complete clone request.
 *
 * @param formValues - The values from the budget clone form
 * @param existing - The existing Budget being cloned
 * @returns A fully populated BudgetCloneFormRequest for the clone operation
 */
function toRequestData(formValues, existing) {
    return {
        id: uuidv7(),
        budgetId: existing.id,
        name: formValues.name,
        startOver: formValues.startOver,
    };
}
